import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionCreatorWithPayload } from '@reduxjs/toolkit'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { pathOr } from 'ramda'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { uiEvents } from '../../../ui'
import { RootState } from 'src/state/types'

const CommissionStatement = (): React.ReactElement => {
  const dispatch = useDispatch()

  const fetchCommissionStatement = useCallback(
    (
      startDate: string,
      endDate: string,
      event: ActionCreatorWithPayload<{ start_date?: string; end_date?: string }>,
    ) => {
      dispatch(
        event({
          start_date: startDate,
          end_date: endDate,
        }),
      )
    },
    [dispatch],
  )

  const onClose = useCallback(() => dispatch(uiEvents.redirect('/dashboard')), [dispatch])

  const fetchCommissionStatementJson = useCallback(
    (startDate: string, endDate: string) =>
      fetchCommissionStatement(startDate, endDate, accountingApiEvents.commissionStatementJson_request),
    [fetchCommissionStatement],
  )

  const fetchCommissionStatementPdf = useCallback(
    (startDate: string, endDate: string) =>
      fetchCommissionStatement(startDate, endDate, accountingApiEvents.commissionStatementPdf_request),
    [fetchCommissionStatement],
  )

  const fetchCommissionStatementCsv = useCallback(
    (startDate: string, endDate: string) =>
      fetchCommissionStatement(startDate, endDate, accountingApiEvents.commissionStatementCsv_request),
    [fetchCommissionStatement],
  )

  const statementData = useSelector((state: RootState) => accountingApiSelectors.getCommissionStatementJson(state))
  const statementPdf = useSelector((state: RootState) => accountingApiSelectors.getCommissionStatementPdf(state))
  const statementCsv = useSelector((state: RootState) => accountingApiSelectors.getCommissionStatementCsv(state))
  const fileName = `${pathOr('', ['context', 'agency', 'name'], statementData) as string} Commission Statement`

  return useMemo(() => {
    return (
      <ViewStatementProvider
        isOpen
        onClose={onClose}
        onParamChange={fetchCommissionStatementJson}
        statementData={statementData}
        downloadStatement={fetchCommissionStatementPdf}
        statementPdf={statementPdf}
        statementCsv={statementCsv}
        pdfFileName={fileName}
        csvFileName={fileName}
        downloadStatementCsv={fetchCommissionStatementCsv}
      >
        <ViewStatement title="Commission Statement" />
      </ViewStatementProvider>
    )
  }, [
    onClose,
    fetchCommissionStatement,
    fetchCommissionStatementPdf,
    fetchCommissionStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    fileName,
  ])
}

export default CommissionStatement
